// Piezas compartidas por los pasos del formulario de inscripción rediseñado (v2).
import React, { useRef, useState } from "react";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import SecurityIcon from "@material-ui/icons/Security";
import LanguageIcon from "@material-ui/icons/Language";
import { useEstilosInscripcion } from "./estilosInscripcion";

// Tarjeta de una sección del paso: ícono + título y los campos en una grilla
export function Seccion({ icono: Icono, titulo, accion, children }) {
  const classes = useEstilosInscripcion();
  return (
    <div className={classes.seccion}>
      <Grid container justify="space-between" alignItems="center">
        <p className={classes.seccionTitulo}>
          <Icono className={classes.seccionIcono} />
          {titulo}
        </p>
        {accion}
      </Grid>
      <Grid container spacing={2}>{children}</Grid>
    </div>
  );
}

// Nota de advertencia bajo los campos de una sección
export function Nota({ children }) {
  const classes = useEstilosInscripcion();
  return (
    <div className={classes.nota}>
      <ReportProblemOutlinedIcon className={classes.notaIcono} />
      <span>{children}</span>
    </div>
  );
}

// Reparte n campos lo más cuadrado posible: 3 por fila en pantallas grandes (4 campos en 2x2)
// y 2 por fila en medianas. Los que quedan solos en la última fila se estiran hasta llenar el ancho.
export function columnas(i, n) {
  const porFilaLg = n === 2 || n === 4 ? 2 : 3;
  const restoLg = n % porFilaLg;
  const lg = restoLg > 0 && i >= n - restoLg ? 12 / restoLg : 12 / porFilaLg;
  const sm = n % 2 === 1 && i === n - 1 ? 12 : 6;
  return { xs: 12, sm, lg };
}

// Pie del formulario: sin banda, integrado al fondo. El enlace a la política de datos sale de la
// configuración (Pol_Dat_Cuc, cargada en InscripcionAgregar como link_politica).
export function PieInscripcion({ linkPolitica }) {
  const classes = useEstilosInscripcion();
  return (
    <footer className={classes.pie}>
      <span>© {new Date().getFullYear()} Universidad de la Costa CUC</span>
      {linkPolitica && (
        <React.Fragment>
          <span className={classes.pieSeparador}>·</span>
          <a href={linkPolitica} target="_blank" rel="noopener noreferrer" className={classes.pieEnlace}>
            <SecurityIcon className={classes.pieIcono} />Política de tratamiento de datos
          </a>
        </React.Fragment>
      )}
      <span className={classes.pieSeparador}>·</span>
      <a href="https://www.cuc.edu.co/" target="_blank" rel="noopener noreferrer" className={classes.pieEnlace}>
        <LanguageIcon className={classes.pieIcono} />cuc.edu.co
      </a>
    </footer>
  );
}

// Carga de un documento en forma de fila (pendiente / cargado / con error).
// El input de archivo vive dentro de la fila y se abre con el botón: no hay inputs ocultos sueltos ni
// búsquedas por id. Valida formato y tamaño al seleccionar o soltar, y entrega el archivo real con onCambiar.
const EXTENSIONES_PERMITIDAS = ["pdf", "jpg", "jpeg", "png"];
export const TAMANO_MAXIMO_MB = 8;

const formatoTamano = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export function CampoArchivo({ id, label, required = false, nombre, archivo, onCambiar, error = false }) {
  const classes = useEstilosInscripcion();
  const inputRef = useRef(null);
  const [problema, setProblema] = useState("");
  const [arrastrando, setArrastrando] = useState(false);

  // Cargado solo si hay nombre (estado del formulario) y el archivo real
  const cargado = Boolean(nombre) && archivo && typeof archivo === "object";

  const recibir = (file) => {
    if (!file) return;
    const extension = file.name.split(".").pop().toLowerCase();
    if (!EXTENSIONES_PERMITIDAS.includes(extension)) {
      setProblema("Formato no permitido: usa PDF, JPG o PNG.");
      return;
    }
    if (file.size > TAMANO_MAXIMO_MB * 1024 * 1024) {
      setProblema(`El archivo supera ${TAMANO_MAXIMO_MB} MB. Si es una foto, tómala con menor resolución o envíala como PDF.`);
      return;
    }
    setProblema("");
    onCambiar(file);
  };

  const estado = problema ? "error" : cargado ? "ok" : error ? "falta" : "pendiente";
  const detalle = problema
    || (cargado ? `${archivo.name} · ${formatoTamano(archivo.size)}`
      : error ? "Debes cargar este documento."
        : `PDF, JPG o PNG · máx. ${TAMANO_MAXIMO_MB} MB`);

  return (
    <div
      className={`${classes.archivoFila} ${estado === "error" || estado === "falta" ? classes.archivoFilaError : ""} ${arrastrando ? classes.archivoFilaArrastre : ""}`}
      onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
      onDragLeave={() => setArrastrando(false)}
      onDrop={(e) => { e.preventDefault(); setArrastrando(false); recibir(e.dataTransfer.files[0]); }}
      {...(estado === "error" || estado === "falta" ? { "data-error": "true" } : {})}
    >
      {estado === "ok" && <CheckCircleIcon className={classes.archivoIconoOk} />}
      {estado === "pendiente" && <RadioButtonUncheckedIcon className={classes.archivoIconoPendiente} />}
      {(estado === "error" || estado === "falta") && <ErrorOutlineIcon className={classes.archivoIconoError} />}

      <div className={classes.archivoTexto}>
        <p className={classes.archivoNombre}>{label}{required ? " *" : ""}</p>
        <p className={`${classes.archivoDetalle} ${estado === "error" || estado === "falta" ? classes.archivoDetalleError : ""}`}>{detalle}</p>
      </div>

      <input
        ref={inputRef}
        id={id}
        type="file"
        hidden
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        onChange={(e) => { recibir(e.target.files[0]); e.target.value = ""; }}
      />
      <div className={classes.archivoAcciones}>
        {cargado && (
          <button type="button" className={classes.archivoQuitar} onClick={() => { setProblema(""); onCambiar(null); }}>
            Quitar
          </button>
        )}
        {/* Botón circular solo con ícono; el texto queda en el tooltip y en aria-label */}
        <Tooltip title={cargado ? "Cambiar archivo" : problema ? "Cargar otro archivo" : "Cargar archivo"}>
          <IconButton
            aria-label={cargado ? `Cambiar ${label}` : `Cargar ${label}`}
            className={cargado ? classes.archivoBotonSecundario : classes.archivoBoton}
            onClick={() => inputRef.current && inputRef.current.click()}
          >
            {cargado ? <SwapHorizIcon /> : <CloudUploadOutlinedIcon />}
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
}
